import React, {
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react"
import { alpha, ClickAwayListener, IconButton, Tooltip } from "@mui/material"
import styled from "@emotion/styled"
import VolumeUpIcon from "@mui/icons-material/VolumeUp"
import StopIcon from "@mui/icons-material/Stop"
import useSound from "hooks/useSound"
import { styles } from "./styles"
import { theme } from "themes/default/theme"
import Controllers from "components/UI/AudioPlayer/controllers"
import AudioSettingsProvider from "contexts/AudioSettingsContext"
import PropTypes from "prop-types"
import * as Speech from "components/TextToSpeech/Speech"

// import { Howler } from "howler"
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => styles.controllers(theme))

const ERROR_MESSAGES = {
  FETCHING_ABORTED: "1",
  NETWORK_ERROR: "2",
  DECODE_ERROR: "3",
  NOT_AVAILABLE: "4",
}

export const SoundButton = forwardRef(
  (
    {
      soundUrl,
      absolute = false,
      size = "medium",
      transparent = false,
      autoPlay = true,
      interrupt = false,
      customSx = {},
      customIconSx = {},
      onSoundLoad,
      color = alpha(theme.palette.activity.main, 0.08),
      iconColor = theme.palette.activity.main,
      disabled = false,
      onPlay,
      onStop,
      onFinishedPlaying,
      soundTranscription = null,
      speechOptions = {},
      invalid = false,
      CustomIcon,
      disableHover = false,
      onStartPlaying,
      onStopPlaying,
      onClickButton = () => { },
      customPlay = false,
      isPlaying: isCustomPlaying = false,
      onTimeUpdate,
    },
    parentRef
  ) => {
    const [play, { load, unload, stop, isPlaying, state, sound }] = useSound(
      null,
      {
        autoPlay: autoPlay,
        interrupt,
      }
    )
    const [prevAutoPlay, setPrevAutoPlay] = React.useState(autoPlay)
    const [firstPlay, setFirstPlay] = React.useState(false)
    const [open, setOpen] = React.useState(false)
    const [audioSettings, setAudioSettings] = React.useState({
      speedLabel: "x1",
      speed: 1,
    })
    const [currentError, setCurrentError] = React.useState(null)
    const [isSpeaking, setIsSpeaking] = React.useState(false)
    const firstPlayRef = useRef(false)

    useImperativeHandle(parentRef, () => ({
      load: (fileURL, config) => load(fileURL, config),
      play: () => playSound(),
      stop: () => stopSound(),
      speak: () => playText(),
      resetFirstPlay: () => setFirstPlay(true),
      soundUrl,
      state,
      soundObj: sound,
    }))

    useEffect(() => {
      firstPlayRef.current = true
    }, [])

    useEffect(() => {
      if (isPlaying) {
        if (onStartPlaying) onStartPlaying()
      } else {
        if (firstPlayRef.current) {
          firstPlayRef.current = false
          return
        }
        if (onStopPlaying) {
          onStopPlaying()
        }
      }
    }, [isPlaying])

    useEffect(() => {
      if (!isPlaying) {
        if (onTimeUpdate) {
          onTimeUpdate(-1)
        }
        return
      }
      if (!onTimeUpdate || !sound) return

      let animationFrameId
      const updateProgress = () => {
        if (typeof sound.seek === "function") {
          const seekTime = sound.seek()
          if (typeof seekTime === "number") {
            onTimeUpdate(seekTime)
          }
        }
        animationFrameId = requestAnimationFrame(updateProgress)
      }

      animationFrameId = requestAnimationFrame(updateProgress)

      return () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId)
        }
      }
    }, [isPlaying, onTimeUpdate, sound])

    useEffect(() => {
      if (soundUrl) {
        load(soundUrl)
      }
      return () => {
        unload()
      }
    }, [])

    useEffect(() => {
      return () => {
        if (soundTranscription !== null) {
          Speech.stop()
        }
      }
    }, [])

    useEffect(() => {
      if (invalid) {
        setCurrentError(ERROR_MESSAGES.NOT_AVAILABLE)
      }
    }, [invalid])

    useEffect(() => {
      if (firstPlay && !isPlaying) {
        setFirstPlay(false)
        if (onFinishedPlaying) onFinishedPlaying()
      }
    }, [isPlaying])

    useEffect(() => {
      if (!state) return
      if (state.loaded && onSoundLoad !== undefined) {
        onSoundLoad(state)
      }
      setFirstPlay(state.loaded && state.shouldPlay)
      if (state?.errorId) {
        setCurrentError(state?.errorMessage)
      } else {
        setCurrentError(null)
      }
    }, [state, onSoundLoad])

    useEffect(() => {
      if (!prevAutoPlay && autoPlay) {
        setPrevAutoPlay(autoPlay)
        // play()
      }
    }, [autoPlay, prevAutoPlay])

    const playText = () => {
      if (
        !soundTranscription ||
        soundTranscription === null ||
        soundTranscription === ""
      ) {
        return
      }
      setIsSpeaking(true)
      const text = soundTranscription
      Speech.speak(text, {
        ...speechOptions,
        onFinished: () => {
          setIsSpeaking(false)
          if (firstPlay && onFinishedPlaying) {
            setFirstPlay(false)
            onFinishedPlaying()
          }
        },
      })
    }

    const playSound = (withEvents = false) => {
      if (
        currentError === ERROR_MESSAGES.NOT_AVAILABLE &&
        soundTranscription !== null
      ) {
        if (!isSpeaking) {
          setIsSpeaking(true)
          const text = soundTranscription
          // console.log("~ text:", text)
          Speech.speak(text, {
            ...speechOptions,
            onFinished: () => {
              setIsSpeaking(false)
            },
          })
        } else {
          setIsSpeaking(false)
          Speech.stop()
        }
      } else {
        setIsSpeaking(false)
        play()
        if (withEvents && onPlay) {
          onPlay()
        }
      }
    }

    const stopSound = (withEvents = false) => {
      if (
        currentError === ERROR_MESSAGES.NOT_AVAILABLE &&
        soundTranscription !== null
      ) {
        Speech.stop()
        setIsSpeaking(false)
      } else {
        setIsSpeaking(false)
        stop()
        if (withEvents && onStop) {
          onStop()
        }
      }
    }

    const onClick = (e) => {
      if (onClickButton) {
        onClickButton(e)
      }
      e.stopPropagation()
      if (customPlay) {
        return
      }
      if (
        currentError === ERROR_MESSAGES.NOT_AVAILABLE &&
        soundTranscription !== null
      ) {
        if (!isSpeaking) {
          setIsSpeaking(true)
          if (onPlay) onPlay()
          const text = soundTranscription
          Speech.speak(text, {
            ...speechOptions,
            onFinished: () => {
              setIsSpeaking(false)
            },
          })
        } else {
          setIsSpeaking(false)
          if (onStop) onStop()
          Speech.stop()
        }
        return
      }
      setIsSpeaking(false)
      if (!isPlaying) {
        if (onPlay) onPlay()
        play()
      } else {
        if (onStop) onStop()
        stop()
      }
    }

    const onSettingsChanged = (newAudioSettings) => {
      if (
        audioSettings.speed !== newAudioSettings.speed ||
        newAudioSettings.volume !== audioSettings.volume
      ) {
        setAudioSettings(newAudioSettings)
        // change sound speed
        sound?.rate(newAudioSettings.speed)
        if (newAudioSettings?.volume >= 0 && newAudioSettings?.volume <= 100) {
          sound?.volume(newAudioSettings.volume / 100)
        }
      }
    }

    return (
      <AudioSettingsProvider onSettingsChanged={onSettingsChanged}>
        {/* <Stack direction={"row"} spacing={0.5}> */}
        <ClickAwayListener
          onClickAway={() => {
            setOpen(false)
          }}
        >
          <HtmlTooltip
            title={<Controllers />}
            leaveDelay={500}
            open={open}
            onOpen={() => {
              if (
                disableHover ||
                disabled ||
                currentError === ERROR_MESSAGES.NOT_AVAILABLE ||
                customPlay
              )
                return
              setOpen(true)
            }}
            onClose={() => setOpen(false)}
            disableFocusListener
            disableTouchListener
            PopperProps={{ disablePortal: true }}
          >
            <IconButton
              id="stop-recording"
              className={`sound-button${isPlaying || isSpeaking || (customPlay && isCustomPlaying)
                ? "-playing"
                : ""
                }`}
              key={soundUrl}
              sx={[
                styles.soundButton,
                absolute && styles.absolute,
                transparent && styles.transparent,
                customSx,
                {
                  backgroundColor: color,
                  "&:hover": {
                    backgroundColor: color,
                  },
                  "&:disabled": {
                    backgroundColor:
                      currentError === ERROR_MESSAGES.NOT_AVAILABLE &&
                        soundTranscription === null
                        ? theme.palette.grey[500]
                        : color,
                  },
                },
                currentError === ERROR_MESSAGES.NOT_AVAILABLE &&
                soundTranscription === null && {
                  backgroundColor: theme.palette.grey[500],
                },
              ]}
              size={size}
              onClick={onClick}
              disableRipple
              disabled={
                disabled ||
                (currentError === ERROR_MESSAGES.NOT_AVAILABLE &&
                  soundTranscription === null)
              }
            >
              {isPlaying || isSpeaking || (customPlay && isCustomPlaying) ? (
                <StopIcon
                  sx={[
                    styles.icon,
                    customIconSx,
                    {
                      color: iconColor,
                    },
                  ]}
                  fontSize={size}
                />
              ) : CustomIcon ? (
                <CustomIcon
                  sx={[
                    styles.icon,
                    customIconSx,
                    {
                      color: iconColor,
                    },
                  ]}
                  fontSize={size}
                />
              ) : (
                <VolumeUpIcon
                  sx={[
                    styles.icon,
                    customIconSx,
                    {
                      color: iconColor,
                    },
                  ]}
                  fontSize={size}
                />
              )}
            </IconButton>
          </HtmlTooltip>
        </ClickAwayListener>
      </AudioSettingsProvider>
    )
  }
)

SoundButton.propTypes = {
  soundUrl: PropTypes.string,
  absolute: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  transparent: PropTypes.bool,
  autoPlay: PropTypes.bool,
  interrupt: PropTypes.bool,
  customSx: PropTypes.object,
  customIconSx: PropTypes.object,
  onSoundLoad: PropTypes.func,
  color: PropTypes.string,
  iconColor: PropTypes.string,
  disabled: PropTypes.bool,
  onPlay: PropTypes.func,
  onStop: PropTypes.func,
  onFinishedPlaying: PropTypes.func,
  soundTranscription: PropTypes.string,
  speechOptions: PropTypes.object,
  invalid: PropTypes.bool,
  CustomIcon: PropTypes.elementType,
  disableHover: PropTypes.bool,
  onStartPlaying: PropTypes.func,
  onStopPlaying: PropTypes.func,
  onClickButton: PropTypes.func,
  customPlay: PropTypes.bool,
  isPlaying: PropTypes.bool,
  onTimeUpdate: PropTypes.func,
}

export default SoundButton
